/** Client upload contract. GB limits are decimal; transport parts are binary MiB. */
// Enabled for development and the next publish by explicit owner approval.
// Per-share opt-in, authentication, review and storage guards still apply.
export const CLIENT_UPLOAD_FEATURE_READY = true;
/** Upload submissions are internal pulses; retain compatibility with client_files rows. */
export function isClientUploadPulse(pulse: { category?: string | null; metadata?: unknown } | null | undefined): boolean {
  const metadata = pulse?.metadata;
  return pulse?.category === 'client_files' ||
    (pulse?.category === 'internal' && !!metadata && typeof metadata === 'object' &&
      'clientUploadSessionId' in metadata &&
      typeof metadata.clientUploadSessionId === 'string' && metadata.clientUploadSessionId.length > 0);
}
export type ClientUploadPartTicket = {
  url: string;
  headers: { 'Content-MD5': string };
  expiresAt: string;
};
export const CLIENT_UPLOAD_LIMITS = {
  fileBytes: 200_000_000_000,
  sessionBytes: 400_000_000_000,
  storageBytes: 500_000_000_000,
  files: 100,
  partBytes: 32 * 1024 * 1024,
  // Across every session/share for a project, not a caller-chosen identity.
  unverifiedProjectBytes: 128 * 1024 * 1024,
  rejectedDays: 14,
  abandonedDays: 7,
} as const;

export type ClientUploadState =
  | 'initializing' | 'uploading' | 'completing' | 'received'
  | 'inspecting' | 'pending_review' | 'approved' | 'copying'
  | 'releasing' | 'available' | 'rejected' | 'deleting' | 'deleted';

export type StructuralFinding = {
  severity: 'neutral' | 'amber' | 'red';
  code: string;
  detail: string;
};
export type StructuralReport = {
  version: 1;
  inspectedAt: string;
  bytesRead: number;
  findings: StructuralFinding[];
  detectedMime?: string;
  detectedExtension?: string;
  media?: { duration?: number; codecs: string[]; format?: string };
};
export type ClientUploadFile = {
  id: string;
  fileName: string;
  sizeBytes: number;
  lastModified: number;
  state: ClientUploadState;
  uploadedBytes: number;
  copiedBytes: number;
  report: StructuralReport | null;
  reviewVersion: number;
  rejectionReason: string | null;
  deleteAfter: string | null;
  error: string | null;
  /** Safe display MIME, not a claim that the file is malware-free. */
  mimeType: string | null;
  uploadedAt: string | null;
  completedParts?: number[];
  completedPartHashes?: Record<string, string>;
};
export type ClientUploadSession = {
  id: string;
  message: string;
  createdAt: string;
  submittedAt: string | null;
  cancelledAt?: string | null;
  pulseId: number | null;
  files: ClientUploadFile[];
  /** Internal-only full-submission eligibility, including files hidden by list filters. */
  allFilesBlue?: boolean;
};
export type ClientUploadList = {
  sessions: ClientUploadSession[];
  nextCursor: string | null;
};
export type ClientUploadReviewList = ClientUploadList & {
  quota: { usedBytes: number; limitBytes: number; binBytes: number };
  canReview: boolean;
};

export function clientUploadRisk(file: Pick<ClientUploadFile, 'report' | 'error'>): StructuralFinding['severity'] {
  if (file.report?.findings.some(f => f.severity === 'red')) return 'red';
  if (!file.report || file.error || file.report.findings.some(f => f.severity === 'amber')) return 'amber';
  return 'neutral';
}

export function expectedUploadPartBytes(size: number, part: number): number {
  if (!Number.isSafeInteger(size) || size < 1 || size > CLIENT_UPLOAD_LIMITS.fileBytes ||
      !Number.isSafeInteger(part) || part < 1 || part > Math.ceil(size / CLIENT_UPLOAD_LIMITS.partBytes)) {
    throw new Error('Invalid upload part');
  }
  return Math.min(CLIENT_UPLOAD_LIMITS.partBytes, size - (part - 1) * CLIENT_UPLOAD_LIMITS.partBytes);
}